/**
 * What on a roster day will cost more than its hours: a premium, a minimum
 * payment, overtime. The duty board shows this before the day is published.
 *
 * The day is read from the board as it stands, grouped into shifts the way the
 * database groups them, so the flags follow an edit without waiting for a save.
 */

import type { BreakSlot, DutySlot } from '../dutyBoardService';
import type { ResolvedRate } from '../payroll/types';
import { roundMoney } from '../payroll/time';
import { buildGuardShifts, rosterGuardIds } from '../rosterCompliance/rosterModel';
import { costRoster } from './costing';
import type {
  CostedShift,
  CostingContext,
  PayTriggerKind,
  ProfitabilitySite,
  RosteredShift,
  RosterPaySettings,
} from './types';

export interface RosterDay {
  siteId: string;
  slotDate: string;
  slots: DutySlot[];
  breakSlots: BreakSlot[];
}

const toWindow = (slot: { start_minute: number; end_minute: number }) => ({
  startMinute: slot.start_minute,
  endMinute: slot.end_minute,
});

/** The shifts a roster day holds, one per unbroken block of a guard's duties and breaks. */
export function rosterDayShifts(day: RosterDay): RosteredShift[] {
  const snapshot = {
    site: { id: day.siteId, name: '', countryCode: '', timezone: '' },
    slotDate: day.slotDate,
    slots: day.slots,
    breakSlots: day.breakSlots,
  };

  return rosterGuardIds(snapshot).flatMap((guardId) => buildGuardShifts(snapshot, guardId).map((shift) => ({
    id: [day.siteId, day.slotDate, guardId, shift.startMinute, shift.endMinute].join(':'),
    guardId,
    guardName: shift.guardName,
    siteId: day.siteId,
    dateKey: day.slotDate,
    startMinute: shift.startMinute,
    endMinute: shift.endMinute,
    dutyWindows: shift.duties.map(toWindow),
    unpaidBreakWindows: shift.breaks.filter((slot) => !slot.is_paid).map(toWindow),
    isPublished: shift.duties.every((slot) => slot.is_published)
      && shift.breaks.every((slot) => slot.is_published),
  })));
}

/**
 * Price one roster day. `otherShifts` is the rest of each guard's week, at any
 * site, so that overtime is judged on the week and not the day; whatever it
 * holds for this site and date is replaced by the day as the board has it.
 */
export function costRosterDay(
  day: RosterDay,
  otherShifts: RosteredShift[],
  context: CostingContext,
): CostedShift[] {
  const dayShifts = rosterDayShifts(day);
  const dayShiftIds = new Set(dayShifts.map((shift) => shift.id));
  const elsewhere = otherShifts.filter((shift) => (
    !(shift.siteId === day.siteId && shift.dateKey === day.slotDate)
  ));

  return costRoster([...elsewhere, ...dayShifts], context)
    .filter((entry) => dayShiftIds.has(entry.shift.id))
    .sort((left, right) => (
      left.shift.startMinute - right.shift.startMinute
      || left.shift.guardName.localeCompare(right.shift.guardName)
    ));
}

/** The order triggers are listed in: guarantees and premiums first, then overtime. */
export const PAY_TRIGGER_KINDS: PayTriggerKind[] = [
  'minimum_shift_pay',
  'unsocial_premium',
  'sunday_premium',
  'public_holiday_premium',
  'overtime',
  'double_time',
  'statutory_rate_floor',
];

export interface PayTriggerTotal {
  kind: PayTriggerKind;
  shiftCount: number;
  amount: number;
  minutes: number;
}

export interface PayTriggerSummary {
  /** Shifts with at least one trigger, in the order given. */
  shifts: CostedShift[];
  totals: PayTriggerTotal[];
  /** What the triggers add to the day's gross pay between them. */
  triggerAmount: number;
  /** Gross pay of every shift given, triggered or not. */
  grossPay: number;
  shiftCount: number;
}

/**
 * Totals per kind of trigger. `kinds` narrows what counts, for a reader who may
 * only be shown the statutory ones.
 */
export function summarisePayTriggers(
  costed: CostedShift[],
  kinds: PayTriggerKind[] = PAY_TRIGGER_KINDS,
): PayTriggerSummary {
  const allowed = new Set(kinds);
  const totals = new Map<PayTriggerKind, PayTriggerTotal>();
  const shifts: CostedShift[] = [];

  costed.forEach((entry) => {
    const triggers = entry.triggers.filter((trigger) => allowed.has(trigger.kind));
    if (triggers.length === 0) return;

    shifts.push(triggers.length === entry.triggers.length ? entry : { ...entry, triggers });
    triggers.forEach((trigger) => {
      const total = totals.get(trigger.kind) ?? { kind: trigger.kind, shiftCount: 0, amount: 0, minutes: 0 };
      total.shiftCount += 1;
      total.amount = roundMoney(total.amount + trigger.amount);
      total.minutes += trigger.minutes;
      totals.set(trigger.kind, total);
    });
  });

  const ordered = PAY_TRIGGER_KINDS
    .map((kind) => totals.get(kind))
    .filter((total): total is PayTriggerTotal => Boolean(total));

  return {
    shifts,
    totals: ordered,
    triggerAmount: roundMoney(ordered.reduce((sum, total) => sum + total.amount, 0)),
    grossPay: roundMoney(costed.reduce((sum, entry) => sum + entry.pay.estimatedTotalPay, 0)),
    shiftCount: costed.length,
  };
}

/** The triggers that follow from the law alone, and so can be shown to someone who may not read pay. */
export const STATUTORY_TRIGGER_KINDS: PayTriggerKind[] = ['minimum_shift_pay', 'unsocial_premium'];

const UNKNOWN_RATE: ResolvedRate = {
  payBandId: null,
  payBandCode: '',
  payBandName: '',
  baseHourlyRate: 0,
  isRateOverride: false,
  isFallback: false,
  employmentType: null,
  payrollReference: null,
  costCentre: null,
  annualLeaveAccrualPercent: 0,
  sundayPremiumMultiplier: 0,
  publicHolidayMultiplier: 0,
  unsocialShiftPremium: 0,
  overtimeDailyMultiplier: 1,
  overtimeWeeklyMultiplier: 1,
  doubleTimeMultiplier: 1,
  minimumShiftPayMinutes: 0,
};

const NO_COMPANY_RULES: RosterPaySettings = {
  baseHourlyRateEur: 0,
  applyMinimumShiftPay: false,
  useSecurityEroUnsocialRates: false,
  unsocialHoursStartMinute: 0,
  unsocialHoursEndMinute: 0,
  unsocialHoursMinMinutes: Number.POSITIVE_INFINITY,
  overtimeEnabled: false,
  overtimeDailyThresholdMinutes: 0,
  overtimeWeeklyThresholdMinutes: 0,
  doubleTimeEnabled: false,
  doubleTimeDailyThresholdMinutes: 0,
  overtimeWeekStartWeekday: 1,
};

/**
 * A context that knows no pay rate and no company rule, for someone who may
 * plan a roster but not read pay. Statutory rules still apply where the site's
 * country has them, so the shifts that trigger one are found; amounts that
 * depend on a rate come out as zero and must not be shown. Summarise with
 * `STATUTORY_TRIGGER_KINDS`.
 */
export function statutoryOnlyContext(
  sites: Map<string, ProfitabilitySite>,
  holidays: Map<string, string>,
): CostingContext {
  return {
    settings: NO_COMPANY_RULES,
    resolveRate: () => UNKNOWN_RATE,
    sites,
    holidays,
    chargeRates: [],
  };
}
